import { QBankStructureStatus, QBankStructureType } from '@exam-management/graphql';
import {
  createFilterOptions,
  Flex,
  FormRadioSelection,
  FormSearchSelection,
  FormTextField,
} from '@shared/components';
import { useFormContext } from 'react-hook-form';

import type { GroupFormFieldSlotProps, GroupOption } from './types';

export interface QBankStructureInformationFormFieldsProps {
  status?: QBankStructureStatus;
  dataTestId?: string;
  slotProps: {
    group: GroupFormFieldSlotProps;
  };
}

const filter = createFilterOptions<GroupOption>();

// NOTE: This component should be used within a FormProvider. It cannot be used standalone.
export default function QBankStructureInformationFormFields(
  props: Readonly<QBankStructureInformationFormFieldsProps>,
) {
  const { status, dataTestId, slotProps } = props;
  const { group } = slotProps;

  // TODO: Add types for formContext
  const formContext = useFormContext();
  const { getValues } = formContext;

  const isNotDraft = status && status !== QBankStructureStatus.Draft;
  const isPublished = status && status === QBankStructureStatus.Published;

  return (
    <Flex
      sx={{ gap: (theme) => theme.spacing(4), flexDirection: 'column' }}
      data-testid={dataTestId}
    >
      <FormTextField
        label='ชื่อคลังข้อสอบ'
        name='name'
        placeholder='ระบุชื่อคลังข้อสอบ'
        fullWidth
        disabled={isPublished}
      />
      <FormTextField
        label='รายละเอียดคลังข้อสอบ'
        name='description'
        placeholder='กรุณากรอกรายละเอียดคลังข้อสอบ'
        fullWidth
        multiline
      />
      <FormRadioSelection
        label='Type'
        name='type'
        options={[
          { label: 'Main', value: QBankStructureType.Main },
          { label: 'Mock', value: QBankStructureType.Mock },
        ]}
        required
        row
        aria-label='type'
        disabled={isNotDraft}
      />
      <FormSearchSelection
        label='Group'
        name='group'
        placeholder='เลือกกลุ่ม / พิมพ์เพื่อสร้างกลุ่มใหม่'
        noOptionsText='No group found'
        loading={group.isLoading}
        options={group.options}
        onChange={(event, value, reason) => {
          if (reason === 'selectOption') {
            group.onChange?.(value?.value?.toString() ?? '');
          } else if (reason === 'clear' && event.type === 'click') {
            group.onClear?.();
          }
        }}
        onInputChange={(_, value, reason) => {
          if (reason === 'input') {
            group.onInputChange?.(value);
          }
        }}
        onOpen={() => {
          group.onOpen?.(getValues('group.value')?.toString() ?? '');
        }}
        onClose={group.onClose}
        filterOptions={(options, params) => {
          if (group.isLoading) {
            return options;
          }
          const filtered = filter(options, params);
          const { inputValue } = params;
          const trimmedInputValue = inputValue.trim();
          const isExisting = options.some((option) => trimmedInputValue === option.label);
          if (trimmedInputValue !== '' && !isExisting) {
            filtered.push({
              label: `Add "${trimmedInputValue}"`,
              inputValue: trimmedInputValue,
              value: trimmedInputValue,
            });
          }
          return filtered;
        }}
        getOptionLabel={(option: GroupOption) => {
          if (typeof option === 'string') {
            return option;
          }
          if (option.inputValue) {
            return option.inputValue;
          }
          return option.label;
        }}
        onBlur={() => {
          group.onBlur?.(getValues('group.value')?.toString() ?? '');
        }}
        selectOnFocus
        clearOnBlur
      />
      <FormTextField label='หมายเหตุ' name='remark' placeholder='กรุณากรอกหมายเหตุ' fullWidth />
    </Flex>
  );
}
